/**
 * Lời gọi API cho MH-32 (báo cáo định kỳ) và MH-33 (thống kê). Cùng khuôn với
 * `thiet-bi-api.ts`: hàm `goi()` gọi fetch trực tiếp qua phong bì nội bộ, dùng
 * cho các đường dẫn CHƯA có trong `@htttn/contracts` (sinh từ OpenAPI 1.0.2) —
 * xem chú thích đầu `apps/api/src/bao-cao/bao-cao.controller.ts` và
 * `apps/api/src/thong-ke/thong-ke.controller.ts` về các đường MỞ RỘNG.
 */
import { layToken } from './khach-api';
// Dùng LẠI cùng một lớp `LoiApi` với `thiet-bi-api.ts`, không khai báo lớp mới —
// nếu không thì `err instanceof LoiApi` ở nơi gọi (so với lớp của tệp kia) sẽ
// luôn sai dù lỗi thật đúng là lỗi API.
import { LoiApi } from './thiet-bi-api';

export { LoiApi };

export interface TrangDuLieu<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

function goc(): string {
  return (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '';
}

interface PhongBi<T> {
  success: boolean;
  code: string;
  message: string;
  data: T | null;
  requestId: string;
}

async function goi<T>(
  duongDan: string,
  tuyChon: { phuongThuc?: string; than?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const token = layToken();
  const res = await fetch(`${goc()}${duongDan}`, {
    method: tuyChon.phuongThuc ?? 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tuyChon.than !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: tuyChon.than !== undefined ? JSON.stringify(tuyChon.than) : undefined,
    signal: tuyChon.signal,
  });
  let pb: PhongBi<T>;
  try {
    pb = (await res.json()) as PhongBi<T>;
  } catch {
    throw new LoiApi('INTERNAL_ERROR', 'Máy chủ trả về dữ liệu không đọc được');
  }
  if (!pb.success) throw new LoiApi(pb.code, pb.message, pb.requestId);
  return pb.data as T;
}

function chuoiTruyVan(tham: Record<string, string | number | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(tham)) {
    if (v === undefined || v === null || v === '') continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

/* ------------------------------- MH-32 -------------------------------------- */

export interface DongChiTieu {
  maChiTieu: string;
  tenChiTieu: string;
  nguon: 'he_thong' | 'khai_bao';
  giaTriHeThong: number | null;
  giaTriDieuChinh: number | null;
  lyDoDieuChinh: string | null;
  giaTriChot: number | null;
}

export interface DongKyBaoCao {
  id: string;
  maBaoCao: string;
  quy: number;
  nam: number;
  trangThai: 'nhap' | 'da_duyet' | 'da_gui';
  nguoiDuyetId: string | null;
  chotLuc: string | null;
  guiLuc: string | null;
}

export interface ChiTietKyBaoCao extends DongKyBaoCao {
  chiTieu: DongChiTieu[];
}

export const baoCaoApi = {
  danhSachKy: (
    tham: { page?: number; pageSize?: number; nam?: number },
    signal?: AbortSignal,
  ): Promise<TrangDuLieu<DongKyBaoCao>> => goi(`/api/v1/reports/periods${chuoiTruyVan(tham)}`, { signal }),

  chiTietKy: (id: string, signal?: AbortSignal): Promise<ChiTietKyBaoCao> =>
    goi(`/api/v1/reports/periods/${id}`, { signal }),

  taoKy: (than: { maBaoCao: string; quy: number; nam: number }): Promise<ChiTietKyBaoCao> =>
    goi('/api/v1/reports/periods', { phuongThuc: 'POST', than }),

  nhapDieuChinh: (
    id: string,
    chiTieu: Array<{ maChiTieu: string; giaTri: number; lyDo: string }>,
  ): Promise<ChiTietKyBaoCao> =>
    goi(`/api/v1/reports/periods/${id}`, { phuongThuc: 'PUT', than: { chiTieu } }),

  tongHopLai: (id: string): Promise<ChiTietKyBaoCao> =>
    goi(`/api/v1/reports/periods/${id}/tong-hop-lai`, { phuongThuc: 'POST', than: {} }),

  chotKy: (id: string): Promise<ChiTietKyBaoCao> =>
    goi(`/api/v1/reports/periods/${id}/chot`, { phuongThuc: 'POST', than: {} }),

  moLaiKy: (id: string, lyDo: string): Promise<ChiTietKyBaoCao> =>
    goi(`/api/v1/reports/periods/${id}/mo-lai`, { phuongThuc: 'POST', than: { lyDo } }),
};

/* ------------------------------- MH-33 -------------------------------------- */

export interface KetQuaThongKe<T> {
  mucDoTongHop: 'tho' | 'gio' | 'ngay';
  dangTongHop: boolean;
  uocTinhHoanThanhGiay: number | null;
  bang: T[];
}

export interface BoLocThongKe {
  tuThoiDiem?: number;
  denThoiDiem?: number;
  nguonId?: string;
  maNhaCungCap?: string;
  loaiLinhVuc?: string;
}

export const thongKeApi = {
  theoDiaBan: (tham: BoLocThongKe, signal?: AbortSignal): Promise<KetQuaThongKe<Record<string, unknown>>> =>
    goi(`/api/v1/reports/devices${chuoiTruyVan(tham as Record<string, string | number>)}`, { signal }),

  theoLinhVuc: (tham: BoLocThongKe, signal?: AbortSignal): Promise<KetQuaThongKe<Record<string, unknown>>> =>
    goi(`/api/v1/reports/bulletins${chuoiTruyVan(tham as Record<string, string | number>)}`, { signal }),

  theoThoiGian: (
    tham: BoLocThongKe,
    signal?: AbortSignal,
  ): Promise<KetQuaThongKe<Record<string, unknown>> & { bangTheoGioTrongNgay: Record<string, unknown>[] }> =>
    goi(
      `/api/v1/reports/bulletins${chuoiTruyVan({ ...tham, nhom: 'thoi_gian' } as Record<string, string | number>)}`,
      {
        signal,
      },
    ),

  theoNhaCungCap: (
    tham: BoLocThongKe,
    signal?: AbortSignal,
  ): Promise<KetQuaThongKe<Record<string, unknown>>> =>
    goi(`/api/v1/statistics/vendors${chuoiTruyVan(tham as Record<string, string | number>)}`, { signal }),

  xuat: (than: {
    loaiBaoCao: 'thiet_bi' | 'ban_tin' | 'tong_hop_ky' | 'nha_cung_cap';
    dinhDang?: 'xlsx' | 'pdf';
    tuThoiDiem?: number;
    denThoiDiem?: number;
    nguonId?: string;
    maNhaCungCap?: string;
    loaiLinhVuc?: string;
    nhom?: string;
    kyId?: string;
  }): Promise<{ id: string }> => goi('/api/v1/reports/export', { phuongThuc: 'POST', than }),

  trangThaiXuat: (
    id: string,
  ): Promise<{
    id: string;
    trangThai: string;
    tienDo: number;
    soDong: number | null;
    urlTaiVe: string | null;
    thongBaoLoi: string | null;
  }> => goi(`/api/v1/reports/export/${id}`),
};
